/**
 * ChatContext — owns the AI chat conversation lifecycle ABOVE the router so a
 * generation survives page navigation (Tier 1) and a reload (Tier 2).
 *
 * Why this lives above <AppRoutes> (see NewUIApp.jsx): the chat page mounts per
 * route, so if state lived in the page it would be destroyed on navigation and
 * the streaming tokens would land in a dead component. Hosting messages + the
 * stream here means:
 *   - Tier 1: navigate away mid-answer and back → the reply is still streaming /
 *     finished, because the provider never unmounted.
 *   - Tier 2: a full reload recovers the completed answer by polling the persisted
 *     ChatSession (the backend finishes + persists even if the client disconnects),
 *     matching on a client-minted `clientTurnId`.
 *
 * The page (AgentPage) stays the owner of composition (attachments, company pin),
 * navigation, the artifact panel and toasts — it registers those as handlers so
 * the provider can fire them WHEN the page is mounted and silently skip them when
 * it isn't. See docs/ai-chat-resilient-generation-plan.md.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { aiAPI } from '../services/api';
import { clearChatImages } from '../services/chatMediaCache';
import { useAuth } from './AuthContext';
import { getReport } from '../data/reports/registry';
import { htmlToPlainText } from '../utils/draftUtils';

export const GREETING = 'Namaste! I\'m Vee — your AI compliance expert.\n\nI can help you with:\n• Drafting board resolutions, minutes, and legal documents\n• Compliance queries under Companies Act, SEBI, FEMA, GST, DPDP\n• Filing deadlines and penalty calculations\n• MCA form guidance for all entity types\n\nWhat are we working on today?';

// localStorage keys for chat continuity.
export const LS_CONSENT_PERSIST = 'cs_ai_consent_persist';
export const LS_CONSENT_TRAIN = 'cs_ai_consent_train';
export const LS_TRANSCRIPT = 'cs_ai_chat_transcript'; // device-local mirror of the live chat
export const LS_SESSION = 'cs_ai_chat_session'; // id of the server-side conversation
const LS_PENDING = 'cs_ai_pending_turn'; // marker for an in-flight turn (reload recovery)
const LS_CHAT_OWNER = 'cs_ai_chat_owner'; // which account the local mirrors belong to
const LS_FORK_PENDING = 'cs_ai_fork_pending'; // this unsaved chat is a branch of an older one
const LS_ORIGIN = 'cs_ai_chat_origin'; // surface that STARTED the live conversation (agent|minutes|report)
// Last conversation each surface was working in, so reopening a studio resumes
// ITS chat instead of a blank one (isolation without amnesia).
export const lastSessionKey = (origin) => `cs_ai_last_session_${origin}`;

// Trust/transparency layer: map the backend's telemetry.toolCalls into human
// "Sources" chips, and flag when computed figures (estimates) were surfaced. Keep
// in sync with the tool names in backend/new-ui-generation/chat/tools.
const TOOL_SOURCE_LABELS = {
  list_my_companies: 'Your companies',
  get_company_deadlines: 'Company deadlines',
  get_portfolio_status: 'Portfolio status',
  get_applicable_forms: 'Applicable forms',
  estimate_penalty: 'Penalty estimate',
  find_professionals: 'Professionals',
};
const ESTIMATE_TOOLS = new Set(['get_company_deadlines', 'get_portfolio_status', 'estimate_penalty']);

// Derive de-duplicated source labels + the estimate flag from a toolCalls list.
export function deriveSources(toolCalls) {
  const names = Array.isArray(toolCalls) ? toolCalls : [];
  const sources = [];
  const seen = new Set();
  let hasEstimate = false;
  for (const n of names) {
    if (ESTIMATE_TOOLS.has(n)) hasEstimate = true;
    const label = TOOL_SOURCE_LABELS[n];
    if (label && !seen.has(label)) { seen.add(label); sources.push(label); }
  }
  return { sources, hasEstimate };
}

// Rebuild the Sources / Estimate chips on each assistant turn so persisted /
// recovered conversations look like live ones. drafts / proposals / attachments
// already come through verbatim.
// Rebuild a persisted report draft's document from its config echo — the
// server stores no report HTML (the deterministic builder lives here).
const rebuildReportDraft = (d) => {
  if (!d || d.kind !== 'report' || String(d.html || '').trim() || !d.reportPrefill) return d;
  try {
    const pf = d.reportPrefill;
    const report = getReport(pf.reportTypeId);
    const cfg = { ...report.defaultConfig, ...(pf.reportConfig || {}) };
    const tabs = report.build(cfg, {}, pf.aiSectionText || {});
    const enabled = report.tabs.filter((t) => typeof t.enabled !== 'function' || t.enabled(cfg));
    const html = enabled
      .map((t, i) => (i ? '<div style="page-break-before:always"></div>' : '') + (tabs[t.id] || ''))
      .join('\n');
    return { ...d, html, plainText: htmlToPlainText(html) };
  } catch { return d; }
};

export function hydratePersistedMessages(msgs) {
  return (Array.isArray(msgs) ? msgs : []).map((m) => {
    if (m.role !== 'assistant') {
      // Server-restored user turns carry attachment metadata only. remote:true
      // on documents tells the lightbox their bytes may be fetchable from
      // ChatAttachment — without it, cross-device restore (this function's
      // whole reason to exist) never even tries. Images rehydrate via the
      // media cache instead.
      if (m.role === 'user' && Array.isArray(m.attachments) && m.attachments.length) {
        return {
          ...m,
          attachments: m.attachments.map((a) => (
            a.fileType !== 'image' && !a.text ? { ...a, remote: true } : a
          )),
        };
      }
      return m;
    }
    const withDrafts = Array.isArray(m.drafts) ? { ...m, drafts: m.drafts.map(rebuildReportDraft) } : m;
    if (!Array.isArray(m.toolCalls) || !m.toolCalls.length) return withDrafts;
    const { sources, hasEstimate } = deriveSources(m.toolCalls);
    return { ...withDrafts, sources, hasEstimate };
  });
}

const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

// Compact a parked version's tail message for the localStorage mirror: keep
// everything the UI needs to re-render the branch, but drop the heavy draft
// HTML (the live branch in the main array keeps its full HTML; an old branch's
// card still shows its title + text). Keeps multi-version snapshots small
// enough that the mirror write never fails on quota.
const compactTailMessage = (m) => ({
  role: m.role,
  content: m.content,
  // catalogId is tiny and load-bearing: it is the resolution's class identity
  // (board / ordinary / special), so dropping it here would let a re-selected
  // old branch version regenerate as a plain board resolution.
  ...(Array.isArray(m.drafts) ? { drafts: m.drafts.map((d) => ({ kind: d.kind, title: d.title, plainText: d.plainText, source: d.source, companyCin: d.companyCin, ...(d.catalogId ? { catalogId: d.catalogId } : {}) })) } : {}),
  ...(m.proposals ? { proposals: m.proposals } : {}),
  ...(m.detailsConfirms ? { detailsConfirms: m.detailsConfirms } : {}),
  ...(m.factsRequests ? { factsRequests: m.factsRequests } : {}),
  ...(m.spreadsheetEdits ? { spreadsheetEdits: m.spreadsheetEdits } : {}),
  ...(m.sources ? { sources: m.sources } : {}),
  ...(m.webSources ? { webSources: m.webSources } : {}),
  ...(m.hasEstimate ? { hasEstimate: m.hasEstimate } : {}),
});
const compactVersions = (versions) =>
  (Array.isArray(versions) ? versions : []).map((v) => ({
    content: v.content,
    tail: Array.isArray(v.tail) ? v.tail.filter((m) => !m.loading).map(compactTailMessage) : null,
  }));

// ── Pending-turn marker helpers (Tier 2) ──
const readPending = () => {
  try { const raw = localStorage.getItem(LS_PENDING); return raw ? JSON.parse(raw) : null; } catch { return null; }
};
const writePending = (marker) => { try { localStorage.setItem(LS_PENDING, JSON.stringify(marker)); } catch { /* quota */ } };
const clearPending = () => { try { localStorage.removeItem(LS_PENDING); } catch { /* ignore */ } };

// Find the assistant reply for a pending turn inside a session's messages: prefer
// the exact clientTurnId. The userText fallback (assistant turn right after the
// matching user message) is only safe when we KNOW this is the right session — in
// the title-matched path (requireId) a same-worded older session could otherwise
// return the wrong answer, so we require the id there.
const findRecoveredReply = (messages, marker, { requireId = false } = {}) => {
  const list = Array.isArray(messages) ? messages : [];
  const byId = list.find((m) => m.role === 'assistant' && m.clientTurnId && m.clientTurnId === marker.clientTurnId);
  if (byId) return byId;
  if (requireId) return null;
  const want = String(marker.userText || '').trim();
  let result = null;
  for (let i = 0; i < list.length; i += 1) {
    if (list[i].role === 'user' && String(list[i].content || '').trim() === want) {
      const next = list[i + 1];
      if (next && next.role === 'assistant' && next.content) result = next;
    }
  }
  return result;
};

const ChatContext = createContext(null);

export const useChat = () => {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChat must be used within ChatProvider');
  return ctx;
};

export const ChatProvider = ({ children }) => {
  // Restore the last conversation (Claude/GPT-style) from the local mirror; fall
  // back to the greeting for a fresh start. Gated on persistence consent.
  const [messages, setMessages] = useState(() => {
    try {
      if (localStorage.getItem(LS_CONSENT_PERSIST) !== '0') {
        const raw = localStorage.getItem(LS_TRANSCRIPT);
        const parsed = raw ? JSON.parse(raw) : null;
        if (Array.isArray(parsed) && parsed.some((m) => m.role === 'user' || m.seeded)) return parsed;
      }
    } catch { /* ignore corrupt/oversized state */ }
    return [{ role: 'assistant', content: GREETING }];
  });
  const [loading, setLoading] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [sessionId, setSessionId] = useState(() => {
    try { return localStorage.getItem(LS_SESSION) || null; } catch { return null; }
  });
  // Persistence (history) is ON by default; training stays OFF (stricter consent).
  const [consentPersist, setConsentPersist] = useState(() => localStorage.getItem(LS_CONSENT_PERSIST) !== '0');
  const [consentTrain, setConsentTrain] = useState(() => localStorage.getItem(LS_CONSENT_TRAIN) === '1');

  // "Branch to a new chat" marks the not-yet-saved conversation as a fork so the
  // server can title it "[Fork] …" on create. Ref + LS mirror (survives reload);
  // cleared the moment ANY session id attaches (fork saved, or an old chat opened).
  const forkPendingRef = useRef((() => {
    try { return localStorage.getItem(LS_FORK_PENDING) === '1'; } catch { return false; }
  })());
  const setForkPending = useCallback((on) => {
    forkPendingRef.current = !!on;
    try {
      if (on) localStorage.setItem(LS_FORK_PENDING, '1');
      else localStorage.removeItem(LS_FORK_PENDING);
    } catch { /* ignore */ }
  }, []);
  const markForked = useCallback(() => setForkPending(true), [setForkPending]);

  // Which surface the LIVE conversation belongs to ('agent' | 'minutes' |
  // 'report'; null = fresh/unknown). Agent-origin chats are shared across
  // every surface (the "same chat as the AI Agent page" contract); a studio
  // dock uses this to avoid adopting the OTHER studio's conversation.
  // Mirrored to localStorage so the rule survives a reload.
  const [conversationOrigin, setConversationOriginState] = useState(() => {
    try { return localStorage.getItem(LS_ORIGIN) || null; } catch { return null; }
  });
  const originRef = useRef(conversationOrigin);
  const setConversationOrigin = useCallback((o) => {
    originRef.current = o || null;
    setConversationOriginState(o || null);
    try {
      if (o) localStorage.setItem(LS_ORIGIN, o);
      else localStorage.removeItem(LS_ORIGIN);
    } catch { /* ignore */ }
  }, []);

  // On a device that has never made an explicit choice, seed consent from the
  // account-level defaults (User.aiPreferences, set in Profile → Privacy).
  // Capture "had a stored choice" BEFORE the mirror effects below write the
  // keys on first mount; user loads async, so seed once when it arrives.
  const { user } = useAuth();
  const hadStoredConsentRef = useRef({
    persist: (() => { try { return localStorage.getItem(LS_CONSENT_PERSIST) !== null; } catch { return true; } })(),
    train: (() => { try { return localStorage.getItem(LS_CONSENT_TRAIN) !== null; } catch { return true; } })(),
  });
  const consentSeededRef = useRef(false);

  // Chat continuity is device-local — but never across ACCOUNTS. If a
  // different user signs in on this device, drop the previous account's local
  // transcript mirror, session pointer, and consent choices so one user never
  // sees (or inherits the settings of) another's conversation. Runs before
  // the consent-seeding effect below so the new user's profile defaults apply.
  // NOTE: the auth user object is shape-shifty — login responses carry `id`,
  // profile refreshes carry `_id`. Check both or the effect misses entirely.
  const chatOwnerUid = String(user?._id || user?.id || '') || null;
  useEffect(() => {
    const uid = chatOwnerUid;
    if (!uid) return;
    let prevOwner = null;
    try { prevOwner = localStorage.getItem(LS_CHAT_OWNER); } catch { /* ignore */ }
    if (prevOwner && prevOwner !== uid) {
      try {
        localStorage.removeItem(LS_TRANSCRIPT);
        localStorage.removeItem(LS_SESSION);
        localStorage.removeItem(LS_PENDING);
        localStorage.removeItem(LS_FORK_PENDING);
        localStorage.removeItem(LS_CONSENT_PERSIST);
        localStorage.removeItem(LS_CONSENT_TRAIN);
      } catch { /* ignore */ }
      // The previous account's image bytes must not survive into this one
      // (D-1a) — same boundary as the transcript mirror above.
      clearChatImages();
      setMessages([{ role: 'assistant', content: GREETING }]);
      setSessionId(null);
      forkPendingRef.current = false;
      setConsentPersist(true);
      setConsentTrain(false);
      hadStoredConsentRef.current = { persist: false, train: false };
      consentSeededRef.current = false;
    }
    try { localStorage.setItem(LS_CHAT_OWNER, uid); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOwnerUid]);

  useEffect(() => {
    if (consentSeededRef.current || !user?.aiPreferences) return;
    consentSeededRef.current = true;
    if (!hadStoredConsentRef.current.persist) setConsentPersist(user.aiPreferences.saveHistory !== false);
    if (!hadStoredConsentRef.current.train) setConsentTrain(user.aiPreferences.allowTraining === true);
  }, [user]);

  // Live mirror for async guards (a stream's callbacks outlive renders and must
  // see the latest loading state, not their captured closure).
  const loadingRef = useRef(loading);
  useEffect(() => { loadingRef.current = loading; }, [loading]);
  const abortRef = useRef(null);
  // Set synchronously the first time we add the reconnecting placeholder, so React
  // StrictMode's setup→cleanup→setup double-invoke (which shares one render's
  // closures, defeating a state/closure check) can't append it twice.
  const recoveryPlaceholderRef = useRef(false);
  // Flipped when the user moves on (sends a new turn / starts a new chat) so the
  // reload-recovery poll stops and never clobbers the newer message. A ref (not
  // message-position) avoids racing the not-yet-committed placeholder append.
  const cancelRecoveryRef = useRef(false);

  // Page-supplied handlers (navigation, artifact panel, toasts, scroll). They fire
  // only while AgentPage is mounted; cleared on unmount so the provider never
  // drives a dead page.
  const pageHandlersRef = useRef({});
  const registerPageHandlers = useCallback((handlers) => {
    pageHandlersRef.current = handlers || {};
    return () => { pageHandlersRef.current = {}; };
  }, []);

  // Going private wipes the device image cache too (D-1a) — "this chat won't
  // be saved" must include the pixels already sitting in IndexedDB, not just
  // the transcript mirror. Runs on the flip to private (and on mounting
  // already-private, which is an idempotent no-op).
  useEffect(() => {
    if (!consentPersist) clearChatImages();
  }, [consentPersist]);

  // ── localStorage mirrors ──
  useEffect(() => { try { localStorage.setItem(LS_CONSENT_PERSIST, consentPersist ? '1' : '0'); } catch { /* ignore */ } }, [consentPersist]);
  useEffect(() => { try { localStorage.setItem(LS_CONSENT_TRAIN, consentTrain ? '1' : '0'); } catch { /* ignore */ } }, [consentTrain]);

  // Mirror the live conversation so navigating back / reloading reopens it. Gated
  // on persistence consent: going private clears the local mirror immediately.
  useEffect(() => {
    try {
      if (!consentPersist) { localStorage.removeItem(LS_TRANSCRIPT); return; }
      // A seeded document (docs-as-artifacts) counts as a started chat too.
      const started = messages.some((m) => m.role === 'user' || m.seeded);
      if (!started) { localStorage.removeItem(LS_TRANSCRIPT); return; }
      // Strip transient bubbles (loading / recovery placeholders); keep drafts /
      // proposals / attachment chips so documents and chips come back on restore.
      const snapshot = messages
        .filter((m) => !m.loading)
        .map((m) => ({
          role: m.role,
          content: m.content,
          ...(m.drafts ? { drafts: m.drafts } : {}),
          ...(m.seeded ? { seeded: true } : {}),
          ...(m.proposals ? { proposals: m.proposals } : {}),
          ...(m.detailsConfirms ? { detailsConfirms: m.detailsConfirms } : {}),
          ...(m.factsRequests ? { factsRequests: m.factsRequests } : {}),
          ...(m.spreadsheetEdits ? { spreadsheetEdits: m.spreadsheetEdits } : {}),
          // remote:true on restored documents — their bytes may be fetchable
          // from ChatAttachment (S3/local), unlike images which rehydrate via
          // the device cache / chat media route. The lightbox keys off it.
          ...(m.attachments ? {
            attachments: m.attachments.map((a) => ({
              id: a.id, name: a.name, fileType: a.fileType,
              ...(a.fileType !== 'image' ? { remote: true } : {}),
            })),
          } : {}),
          ...(m.sources ? { sources: m.sources } : {}),
          ...(m.webSources ? { webSources: m.webSources } : {}),
          ...(m.hasEstimate ? { hasEstimate: m.hasEstimate } : {}),
          // Edited-message versions (ChatGPT-style ‹ x/y ›) — parked branches
          // carry their tail messages so flipping back restores the old thread.
          // Tails are compacted (no full draft HTML) so several versions can't
          // bloat the snapshot past the localStorage quota — if the mirror
          // write failed, the server's linear log would replace it and FLATTEN
          // the versions into separate turns on the next restore.
          ...(m.versions ? { versions: compactVersions(m.versions), current: m.current } : {}),
        }));
      localStorage.setItem(LS_TRANSCRIPT, JSON.stringify(snapshot));
    } catch { /* quota / serialization — the server copy is the durable one */ }
  }, [messages, consentPersist]);

  // Remember the server-side conversation id so a fresh device/tab can reopen it.
  useEffect(() => {
    try {
      if (consentPersist && sessionId) localStorage.setItem(LS_SESSION, sessionId);
      else localStorage.removeItem(LS_SESSION);
    } catch { /* ignore */ }
  }, [sessionId, consentPersist]);

  // …and remember it PER SURFACE, so reopening a studio resumes the chat that
  // belongs to it rather than a blank one (the dock reads this on arrival).
  useEffect(() => {
    try {
      if (consentPersist && sessionId && conversationOrigin) {
        localStorage.setItem(lastSessionKey(conversationOrigin), sessionId);
      }
    } catch { /* ignore */ }
  }, [sessionId, conversationOrigin, consentPersist]);

  // Once a session id attaches, the fork mark has served its purpose (the fork
  // was saved under it) — or the user opened an existing chat, which is not a
  // fork. Either way, drop it.
  useEffect(() => { if (sessionId) setForkPending(false); }, [sessionId, setForkPending]);

  // Replace the trailing (assistant) bubble — used by the stream + recovery.
  const setTrailing = useCallback((msg) => {
    setMessages((prev) => {
      if (!prev.length) return [msg];
      const next = [...prev];
      next[next.length - 1] = msg;
      return next;
    });
  }, []);

  const fireScroll = useCallback(() => { try { pageHandlersRef.current.onScroll?.(); } catch { /* ignore */ } }, []);

  // Apply the final reply envelope (works for streaming `done` and non-streaming).
  const applyEnvelope = useCallback((data, streamedText) => {
    const reply = data?.message || streamedText || 'I encountered an issue. Please try again.';
    const serverDrafts = Array.isArray(data?.drafts) ? data.drafts : [];
    const proposals = Array.isArray(data?.proposals) ? data.proposals : [];
    const allActions = Array.isArray(data?.clientActions) ? data.clientActions : [];
    // build_report directives → client-built report drafts ("client-side
    // deterministic render, server-side intelligence"): the statutory builder
    // lives in the frontend registry, so draft_auditor_report sends config +
    // AI sections and the document is assembled here, then rides the normal
    // draft/artifact flow.
    const reportDrafts = allActions
      .filter((a) => a && a.type === 'build_report')
      .map((a) => {
        try {
          const report = getReport(a.reportTypeId);
          const cfg = a.config || {};
          const tabs = report.build(cfg, {}, a.aiSections || {});
          const enabled = report.tabs.filter((t) => typeof t.enabled !== 'function' || t.enabled(cfg));
          const html = enabled
            .map((t, i) => (i ? '<div style="page-break-before:always"></div>' : '') + (tabs[t.id] || ''))
            .join('\n');
          if (!html.trim()) return null;
          return {
            kind: 'report',
            title: a.title || report.label,
            html,
            plainText: htmlToPlainText(html),
            source: a.source || 'preview',
            companyCin: a.companyCin || null,
            // Handoff payload for "Open in Report Generator" (page prefill).
            reportPrefill: { reportTypeId: report.id, reportConfig: cfg, aiSectionText: a.aiSections || {}, docSource: a.source || 'preview' },
          };
        } catch { return null; }
      })
      .filter(Boolean);
    // Server report drafts with no HTML are persistence stubs (the server
    // stores the config echo; the client owns the builder) — the client-built
    // reportDrafts above are the live documents, so the stubs are dropped.
    const drafts = [
      ...serverDrafts.filter((d) => !(d && d.kind === 'report' && !String(d.html || '').trim())),
      ...reportDrafts,
    ];
    const clientActions = allActions.filter((a) => !a || a.type !== 'build_report');
    const webSources = Array.isArray(data?.webSources) ? data.webSources : [];
    const detailsConfirms = Array.isArray(data?.detailsConfirms) ? data.detailsConfirms : [];
    const factsRequests = Array.isArray(data?.factsRequests) ? data.factsRequests : [];
    // Workbooks edit_spreadsheet wrote this turn. Each is a download card —
    // dropping them here left Vee announcing a file the user could not get.
    const spreadsheetEdits = Array.isArray(data?.spreadsheetEdits) ? data.spreadsheetEdits : [];
    // Company corrections applied without a confirmation card (set_company_details).
    // Handed to the page so they persist as session overrides — otherwise the
    // next turn would re-render the document from the stored record.
    const overrideUpdates = Array.isArray(data?.companyOverrideUpdates) ? data.companyOverrideUpdates : [];
    const { sources, hasEstimate } = deriveSources(data?.telemetry?.toolCalls);
    if (data?.sessionId) setSessionId(data.sessionId);
    // A reply the server produced because the AI could not be reached is not an answer: it must not be
    // exportable, and a thumbs-down on an outage is not feedback about Vee.
    setTrailing({ role: 'assistant', content: reply, drafts, proposals, sources, hasEstimate, ...(data?.source === 'error' ? { failed: true } : {}), ...(webSources.length ? { webSources } : {}), ...(detailsConfirms.length ? { detailsConfirms } : {}), ...(factsRequests.length ? { factsRequests } : {}), ...(spreadsheetEdits.length ? { spreadsheetEdits } : {}) });
    const h = pageHandlersRef.current;
    try { h.onClientActions?.(clientActions); } catch { /* page may have unmounted */ }
    if (overrideUpdates.length) {
      try { h.onCompanyOverrides?.(overrideUpdates); } catch { /* page may have unmounted */ }
    }
    if (drafts.length) { try { h.onArtifact?.(drafts[0]); } catch { /* ignore */ } }
  }, [setTrailing]);

  /**
   * Send one turn. Composition (userMsg, wire history, company context) is done by
   * the page and passed in; the provider owns the transport + transcript so it
   * survives navigation.
   */
  const sendTurn = useCallback(async ({ userMsg, history, context, consent, sessionId: sid, userText }) => {
    if (loadingRef.current) return;
    // First turn of a FRESH conversation stamps its origin (mirrors the
    // server's first-turn-wins `origin` on ChatSession). An existing session
    // continued with origin unknown (e.g. after a reload) stays unknown —
    // never re-stamped mid-conversation from whatever page the user is on.
    if (!originRef.current && !sid
      && !(Array.isArray(history) && history.filter((m) => m.role === 'user').length > 1)) {
      setConversationOrigin(context?.pageContext?.page || 'agent');
    }
    cancelRecoveryRef.current = true; // user is sending → abandon any reload-recovery poll
    setMessages((prev) => {
      // Drop a dangling reload-recovery placeholder so a new turn replaces it.
      const last = prev[prev.length - 1];
      const base = last && (last.recovering || last.recoverFailed) ? prev.slice(0, -1) : prev;
      return [...base, userMsg, { role: 'assistant', content: '', loading: true }];
    });
    setLoading(true);
    fireScroll();

    const clientTurnId = uid();
    // Pending marker enables reload-recovery — only meaningful when we persist
    // (a private chat stores nothing server-side, so there's nothing to recover).
    if (consent?.persist) writePending({ sessionId: sid || null, clientTurnId, userText, startedAt: Date.now() });

    const ac = new AbortController();
    abortRef.current = ac;
    const opts = { consent, sessionId: sid, clientTurnId, signal: ac.signal };
    // A branched-but-unsaved chat announces itself so the server titles the new
    // session "[Fork] …". Only meaningful before the first save (no session yet).
    const ctx = forkPendingRef.current && !sid ? { ...context, forked: true } : context;

    let streamed = '';
    // The server already emits a `status` line when it goes off to run tools —
    // nothing was listening, so a turn that spends 30s drafting a document
    // showed a bare spinner the whole time. Surfacing it doesn't make the turn
    // faster; it makes the wait legible, which is most of what "it feels slow"
    // actually is (T8.1).
    let statusLine = '';
    try {
      const envelope = await aiAPI.chatStream(history, ctx, opts, {
        onToken: (t) => {
          streamed += t;
          setTrailing({ role: 'assistant', content: streamed, loading: true, status: statusLine });
          fireScroll();
        },
        onStatus: (d) => {
          statusLine = String(d?.message || '');
          setTrailing({ role: 'assistant', content: streamed, loading: true, status: statusLine });
        },
      });
      applyEnvelope(envelope, streamed);
      clearPending();
    } catch (streamErr) {
      if (ac.signal.aborted) {
        // User pressed Stop — finalize with whatever streamed; never fall back.
        setTrailing({ role: 'assistant', content: streamed || '_Stopped._', stopped: true });
        clearPending();
      } else {
        // Streaming unavailable → non-streaming fallback (no abort signal needed).
        try {
          const res = await aiAPI.chat(history, ctx, { consent, sessionId: sid, clientTurnId });
          applyEnvelope(res?.data || res, '');
          clearPending();
        } catch (err) {
          // Limits and feature gates carry a precise server message — show it,
          // not a misleading "can't connect". Only true transport/5xx failures
          // get the generic connectivity text.
          const known = ['BUDGET_EXCEEDED', 'RATE_LIMITED', 'FEATURE_DISABLED'].includes(err?.code)
            || err?.status === 429 || err?.status === 403;
          setTrailing({
            role: 'assistant',
            content: known && err?.message
              ? err.message
              : 'I\'m unable to connect to the AI service right now. Please ensure the backend API is running and configured with an AI provider.\n\nIn the meantime, you can use the Document Library or Resolution Engine for pre-built templates.',
          });
          clearPending();
          try {
            pageHandlersRef.current.onToast?.(
              known && err?.message ? err.message : 'AI service unavailable. Backend integration required.',
              'warning',
            );
          } catch { /* ignore */ }
        }
      }
    } finally {
      setLoading(false);
      abortRef.current = null;
      fireScroll();
    }
  }, [applyEnvelope, fireScroll, setTrailing, setConversationOrigin]);

  const stopGenerating = useCallback(() => { try { abortRef.current?.abort(); } catch { /* ignore */ } }, []);

  // Start a fresh conversation (data side; the page handles UI: artifact/history/focus).
  const resetChat = useCallback(() => {
    cancelRecoveryRef.current = true; // a fresh chat abandons any reload-recovery poll
    setMessages([{ role: 'assistant', content: GREETING }]);
    setSessionId(null);
    setForkPending(false); // a fresh chat is not a fork
    setConversationOrigin(null); // origin is stamped by the next first turn
    clearPending();
    try { localStorage.removeItem(LS_TRANSCRIPT); localStorage.removeItem(LS_SESSION); } catch { /* ignore */ }
  }, [setForkPending, setConversationOrigin]);

  // ── Tier 2: recover a turn that was in flight when the page reloaded ──
  // Runs once when the provider mounts (it lives above the router, so this is once
  // per page load). Written to be safe under React StrictMode's mount→unmount→mount
  // double-invoke: the placeholder append is idempotent and the second mount
  // resumes the (now-cancelled) poll instead of bailing.
  const STALE_MARKER_MS = 15 * 60 * 1000; // ignore markers older than 15 min
  useEffect(() => {
    const marker = readPending();
    if (!marker) return undefined;
    // Private chats persist nothing → nothing to recover.
    if (!consentPersist) { clearPending(); return undefined; }
    // Drop a stale marker (server long since finished, or a permanently
    // unrecoverable turn) so we don't poll forever across reloads.
    if (marker.startedAt && Date.now() - marker.startedAt > STALE_MARKER_MS) { clearPending(); return undefined; }
    // Recover when the transcript ends on an unanswered user turn, OR already shows
    // our recovery placeholder (StrictMode's second mount, or a prior failed try).
    const last = messages[messages.length - 1];
    const resumable = last && (last.role === 'user' || last.recovering || last.recoverFailed);
    if (!resumable) { clearPending(); return undefined; }

    let cancelled = false;
    let timer = null;
    setRecovering(true);
    // Append the placeholder exactly once (ref guard survives StrictMode's double
    // setup); the poll below still re-arms on the second setup so recovery runs.
    // The setState updater also re-checks, belt-and-suspenders.
    if (!recoveryPlaceholderRef.current) {
      recoveryPlaceholderRef.current = true;
      setMessages((prev) => {
        const tail = prev[prev.length - 1];
        if (tail && (tail.recovering || tail.recoverFailed)) return prev;
        return [...prev, { role: 'assistant', content: '', loading: true, recovering: true }];
      });
    }

    const tryFindReply = async () => {
      let sid = marker.sessionId;
      const locatedByTitle = !sid;
      if (!sid) {
        // Brand-new chat reloaded before the id came back — locate by title match.
        // Collapse whitespace the same way the list endpoint's clean() does, so a
        // multi-space question still matches its persisted title.
        const res = await aiAPI.listChatSessions();
        const sessions = res?.data?.sessions || res?.sessions || [];
        const norm = (v) => String(v || '').replace(/\s+/g, ' ').trim().toLowerCase();
        const want = norm(marker.userText).slice(0, 80);
        const cand = sessions.find((s) => norm(s.title) === want)
          || sessions.find((s) => want && norm(s.preview).includes(want.slice(0, 40)));
        if (!cand) return null;
        sid = cand.id;
      }
      const res = await aiAPI.getChatSession(sid);
      const session = res?.data?.session || res?.session;
      const found = findRecoveredReply(session?.messages, marker, { requireId: locatedByTitle });
      return found ? { reply: found, sessionId: sid } : null;
    };

    // Stop if this poll was torn down (StrictMode cleanup) or the user moved on
    // (sent a new turn / new chat). Ref-based so we never race the not-yet-
    // committed placeholder append the way a message-position check did.
    const stop = () => cancelled || cancelRecoveryRef.current;

    // Resolve recovery by stripping EVERY recovery placeholder (defends against an
    // orphan from any double-append) and appending the final bubble exactly once.
    const resolveRecovery = (finalMsg) => setMessages((prev) => {
      const cleaned = prev.filter((m) => !m.recovering && !m.recoverFailed);
      return [...cleaned, finalMsg];
    });

    const deadline = Date.now() + 60000; // give the server up to ~60s
    // Poll quickly at first (the answer often lands within a few seconds), then
    // back off so a long generation doesn't hammer the endpoint.
    let attempt = 0;
    const nextDelay = () => (attempt < 6 ? 600 : attempt < 14 ? 1200 : 2500);
    const poll = async () => {
      if (stop()) { setRecovering(false); return; }
      try {
        const hit = await tryFindReply();
        if (stop()) { setRecovering(false); return; }
        if (hit) {
          const hydrated = hydratePersistedMessages([hit.reply])[0];
          setSessionId(hit.sessionId);
          resolveRecovery({
            role: 'assistant',
            content: hydrated.content || '',
            ...(hydrated.drafts ? { drafts: hydrated.drafts } : {}),
            ...(hydrated.proposals ? { proposals: hydrated.proposals } : {}),
            ...(hydrated.detailsConfirms ? { detailsConfirms: hydrated.detailsConfirms } : {}),
            ...(hydrated.factsRequests ? { factsRequests: hydrated.factsRequests } : {}),
            ...(hydrated.sources ? { sources: hydrated.sources } : {}),
            ...(hydrated.hasEstimate ? { hasEstimate: hydrated.hasEstimate } : {}),
          });
          clearPending();
          setRecovering(false);
          fireScroll();
          return;
        }
      } catch { /* transient — keep polling */ }
      if (stop()) { setRecovering(false); return; }
      if (Date.now() > deadline) {
        // Keep the marker so a later reload retries; the failed placeholder is
        // loading:true so it stays out of the persisted mirror.
        resolveRecovery({ role: 'assistant', content: '', loading: true, recoverFailed: true });
        setRecovering(false);
        return;
      }
      attempt += 1;
      timer = setTimeout(poll, nextDelay());
    };
    poll();

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(() => ({
    messages,
    setMessages,
    loading,
    sessionId,
    setSessionId,
    consentPersist,
    setConsentPersist,
    consentTrain,
    setConsentTrain,
    recovering,
    sendTurn,
    stopGenerating,
    resetChat,
    markForked,
    registerPageHandlers,
    conversationOrigin,
    setConversationOrigin,
  }), [messages, loading, sessionId, consentPersist, consentTrain, recovering, sendTurn, stopGenerating, resetChat, markForked, registerPageHandlers, conversationOrigin, setConversationOrigin]);

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
};

export default ChatContext;
